import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ExecutiveReportService, ReportFrequency } from '@vendasta/executive-report';
import { GalaxyDatePipe } from '@vendasta/galaxy/pipes';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { formatDateRange, subtractDays } from '@vendasta/galaxy/utility/date-utils';
import { format } from 'date-fns';
import { combineLatest, Observable } from 'rxjs';
import { map, startWith, switchMap, take } from 'rxjs/operators';
import { PartnerService } from '../../../../../../core/partner.service';

@Component({
  templateUrl: './executive-report-sender.component.html',
  styleUrls: ['./executive-report-sender.component.scss'],
  standalone: false,
})
export class ExecutiveReportSenderComponent implements OnInit {
  accountGroupId: string;
  vbcUrl: string;
  partnerId: string;
  companyName: string;
  reportDateOptions$: Observable<any>;

  dateSelector = new UntypedFormControl();
  durationOptions = [
    { label: 'Monthly', value: true },
    { label: 'Weekly', value: false },
  ];

  datePipe = new GalaxyDatePipe();

  static config(accountGroupId: string, partnerId: string, companyName: string): MatDialogConfig {
    const conf = new MatDialogConfig();
    conf.autoFocus = false;
    conf.minWidth = 400;
    conf.data = {
      accountGroupId: accountGroupId,
      partnerId: partnerId,
      companyName: companyName,
    };
    return conf;
  }

  constructor(
    public dialogRef: MatDialogRef<ExecutiveReportSenderComponent>,
    private execReportService: ExecutiveReportService,
    @Inject(MAT_DIALOG_DATA) data: any,
    private httpClient: HttpClient,
    private partnerService: PartnerService,
    private snackbarService: SnackbarService,
  ) {
    this.accountGroupId = data.accountGroupId;
    this.vbcUrl = data.vbcUrl;
    this.partnerId = data.partnerId;
    this.companyName = data.companyName;
  }

  ngOnInit(): void {
    this.reportDateOptions$ = combineLatest([this.getReportDates(true), this.getReportDates(false)]).pipe(
      map(([monthly, weekly]) => {
        return [
          { label: 'Monthly', options: monthly },
          { label: 'Weekly', options: weekly },
        ];
      }),
      startWith([
        [
          { label: 'Monthly', value: [] },
          { label: 'Weekly', value: [] },
        ],
      ]),
    );
  }

  getReportDates(monthly: boolean): Observable<{ label: string; value: { period: string; date: Date } }[]> {
    return this.execReportService
      .listReportDates(
        this.accountGroupId,
        monthly ? ReportFrequency.MONTHLY : ReportFrequency.WEEKLY,
        null,
        monthly ? 12 : 52,
      )
      .pipe(
        map((resp) =>
          resp?.results?.map((date) => {
            return {
              label: monthly
                ? format(subtractDays(date, 1), 'LLLL yyyy')
                : formatDateRange({ start: subtractDays(date, 6), end: date }),
              value: { period: monthly ? 'monthly' : 'weekly', date: date },
            };
          }),
        ),
      );
  }

  sendReport(): void {
    this.partnerService
      .getBusinessCenterHostname()
      .pipe(
        take(1),
        switchMap((vbcUrl) => {
          const url = vbcUrl + '/internalApi/v3/resendExecutiveReportActivity/';
          return this.httpClient.post(url, null, {
            withCredentials: true,
            params: {
              partnerId: this.partnerId,
              accountGroupId: this.accountGroupId,
              frequency: this.dateSelector.value.period,
              companyName: this.companyName,
              reportDateDateTime: this.dateSelector.value.date.toISOString(),
            },
          });
        }),
      )
      .subscribe(
        () => {
          this.dialogRef.close(true);
        },
        () => {
          this.snackbarService.openErrorSnack('Error');
          this.dialogRef.close(false);
        },
      );
  }
}
